import type { InvoicePart } from '../../../types/invoice';
import type { LineItemField } from './invoiceDetailFormTypes';

export const MAX_DERIVED_UNIT_PRICE_DECIMALS = 6;
export const MAX_LINE_TOTAL_DECIMALS = 2;
export const CALCULATION_MISMATCH_TOLERANCE = 0.01;

export function invoicePartLineTotalNumber(p: InvoicePart): number {
    const v = p.totalBuyPrice ?? (p as { buyPrice?: number }).buyPrice;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
}

/** When qty and line total disagree in sign, align qty to total (load) or total to qty (user edited qty). */
export function reconcileQtyTotalSameSign(item: InvoicePart, lastEdited: 'qty' | 'totalBuyPrice'): InvoicePart {
    const q = Number(item.qty);
    const t = invoicePartLineTotalNumber(item);
    if (!Number.isFinite(q) || !Number.isFinite(t)) return item;
    if (q === 0 || t === 0 || Math.sign(q) === Math.sign(t)) return item;
    if (lastEdited === 'totalBuyPrice') {
        return { ...item, qty: Math.sign(t) * Math.abs(q) };
    }
    return { ...item, totalBuyPrice: Math.sign(q) * Math.abs(t) };
}

export function roundToMaxDecimals(value: number, maxDecimals: number): number {
    const factor = 10 ** maxDecimals;
    return Math.round(value * factor) / factor;
}

export function tryDeriveUnitBuyPrice(qty: unknown, totalBuyPrice: unknown): number | undefined {
    const q = Number(qty);
    const total = Number(totalBuyPrice);
    if (!Number.isFinite(q) || !Number.isFinite(total)) return undefined;
    if (Math.abs(q) < 1e-9) return undefined;
    return roundToMaxDecimals(Math.abs(total) / Math.abs(q), MAX_DERIVED_UNIT_PRICE_DECIMALS);
}

export function applyAutoDerivedUnitPrice(
    item: InvoicePart,
    field: LineItemField,
    normalizedValue: unknown,
): InvoicePart {
    const updated: InvoicePart = { ...item, [field]: normalizedValue } as InvoicePart;
    if (field === 'unitBuyPrice') {
        const n = Number(normalizedValue);
        const unit = Number.isFinite(n) ? Math.abs(n) : 0;
        const q = Number(updated.qty);
        const next: InvoicePart = {
            ...updated,
            unitBuyPrice: unit,
        };
        if (Number.isFinite(q)) {
            next.totalBuyPrice = roundToMaxDecimals(q * unit, MAX_LINE_TOTAL_DECIMALS);
        }
        return next;
    }
    if (field === 'qty' || field === 'totalBuyPrice') {
        const reconciled = reconcileQtyTotalSameSign(updated, field);
        const derivedUnit = tryDeriveUnitBuyPrice(reconciled.qty, invoicePartLineTotalNumber(reconciled));
        if (derivedUnit !== undefined) {
            return { ...reconciled, unitBuyPrice: derivedUnit };
        }
        return reconciled;
    }
    return updated;
}

export function normalizeItemsUnitPriceFromAmountAndQty(items: InvoicePart[] | undefined): InvoicePart[] {
    return (items ?? []).map((item) => {
        const reconciled = reconcileQtyTotalSameSign({ ...item }, 'totalBuyPrice');
        const derivedUnit = tryDeriveUnitBuyPrice(reconciled.qty, invoicePartLineTotalNumber(reconciled));
        const rawU = Number(reconciled.unitBuyPrice);
        const fallbackUnit = Number.isFinite(rawU) ? Math.abs(rawU) : 0;
        const nextUnit = derivedUnit !== undefined ? derivedUnit : fallbackUnit;
        const next = { ...reconciled, unitBuyPrice: nextUnit } as InvoicePart;
        const unchanged =
            next.qty === item.qty &&
            invoicePartLineTotalNumber(next) === invoicePartLineTotalNumber(item) &&
            Math.abs((item.unitBuyPrice ?? 0) - (next.unitBuyPrice ?? 0)) < 0.0000005;
        return unchanged ? item : next;
    });
}

export function isReturnInvoiceType(value: unknown): boolean {
    return value === 'returned' || value === 'return_slip';
}

export function normalizeReturnSignedNumber(value: unknown): number {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return -Math.abs(n);
}
